import Image from '@/components/Image';
import Link from '@/components/Link';
import { COLORS } from '@/styles/color';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { ImageProps } from 'next/image';
import React, { FC } from 'react';
import styled from 'styled-components';

export interface ResourceCardProps {
  image?: ImageProps;
  eyebrow: string;
  title: string;
  href: string;
  onClick?: () => void;
  readMoreOverride?: string;
}
const ResourceCard: FC<ResourceCardProps> = (props) => {
  return (
    <Card>
      {props.image && (
        <CardImage>
          <Image {...props.image} fill />

          <Link href={props.href}>
            <a onClick={props.onClick} />
          </Link>
        </CardImage>
      )}

      <CardContent>
        <CardEyebrow>{props.eyebrow}</CardEyebrow>

        <Link href={props.href}>
          <a onClick={props.onClick}>
            <CardTitle>{props.title}</CardTitle>
          </a>
        </Link>

        <Link href={props.href}>
          <ReadMoreLink onClick={props.onClick}>
            {props.readMoreOverride ?? 'Read more'}{' '}
            <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.336 0.736515L11.072 5.45652L6.352 10.1765L5.184 9.00852L8.72 5.45652L7.776 4.51252H9.712V6.41652H0.528V4.51252H7.776L5.168 1.90452L6.336 0.736515Z"
                fill={COLORS.BLACK}
              />
            </svg>
          </ReadMoreLink>
        </Link>
      </CardContent>
    </Card>
  );
};

export default ResourceCard;

// Styled Components

const Card = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  flex: 1 0 0;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  background: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};

  box-shadow: 0px 0px 20px 0px rgba(0, 27, 56, 0.1);

  a {
    color: inherit;
    text-decoration: none;
  }
`;

const CardImage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: ${remCalc(10, 10, 0, 0)};
  overflow: hidden;

  img {
    object-fit: cover;
    object-position: center;
  }

  a {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
`;

const CardContent = styled.div`
  padding: ${remCalc(20, 20, 40)};
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  ${mediaBreakpointUp('tablet')} {
    padding-bottom: ${remCalc(50)};
  }
`;

const CardEyebrow = styled.p`
  font-weight: ${FONT_WEIGHTS.BOLD};
  ${customTextSize(12, 20)}
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0 0 ${remCalc(10)} 0;
`;

const CardTitle = styled.p`
  margin-bottom: 1rem;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(24, 30)};
  }
`;

const ReadMoreLink = styled.a`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: auto;
  font-weight: ${FONT_WEIGHTS.BOLD};
  ${customTextSize(16, 20)}
  margin: auto 0 0 0;
  color: ${COLORS.BLACK};
  text-decoration: none;

  svg {
    width: 14px;
    height: auto;
  }
`;
